<!-- SwipFx Orders Page (Bootstrap 5) -->
<!-- Tabs need bootstrap.bundle.js -->
<div class="min-vh-100 bg-light">
    <app-header [page]="'Admin'"></app-header>
    <main class="container py-4">

  <!-- Page Header -->
  <div class="d-flex align-items-center justify-content-between mb-4">
    <div>
      <h4 class="fw-bold mb-0">Users & Staff</h4>
      <div class="text-muted small">Create and manage role-scoped back-office accounts</div>
    </div>
  </div>

  <div class="row g-4">

    <!-- LEFT: Add User -->
    <div class="col-12 col-lg-4">
      <div class="card shadow-sm border-0">
        <div class="card-body">

          <h6 class="fw-semibold mb-3">Add User</h6>

          <form [formGroup]="adminForm" (ngSubmit)="addAdmin()">

            <div class="mb-3">
              <label class="form-label small text-muted">First Name</label>
              <input type="text"
                     class="form-control"
                     formControlName="firstName"
                     placeholder="e.g. John"
                     [ngClass]="{ 'is-invalid': isInvalid(adminForm, 'firstName') }" />
              <div *ngIf="isInvalid(adminForm, 'firstName')" class="invalid-feedback">
                First name is required
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label small text-muted">Last Name</label>
              <input type="text"
                     class="form-control"
                     formControlName="lastName"
                     placeholder="e.g. Doe"
                     [ngClass]="{ 'is-invalid': isInvalid(adminForm, 'lastName') }" />
              <div *ngIf="isInvalid(adminForm, 'lastName')" class="invalid-feedback">
                Last name is required
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label small text-muted">Email</label>
              <input type="email"
                     class="form-control"
                     formControlName="email"
                     placeholder="admin@swipfx.com"
                     [ngClass]="{ 'is-invalid': isInvalid(adminForm, 'email') }" />
              <div *ngIf="isInvalid(adminForm, 'email')" class="invalid-feedback">
                <div *ngIf="adminForm.get('email')?.errors?.['required']">Email is required</div>
                <div *ngIf="adminForm.get('email')?.errors?.['email']">Enter a valid email</div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label small text-muted">Country</label>
              <select class="form-select country-select"
                      formControlName="country"
                      autocomplete="country"
                      (change)="onAdminCountryChanged()"
                      [ngClass]="{ 'is-invalid': isInvalid(adminForm, 'country') }">
                <option value="" disabled>
                  {{ countriesLoading ? 'Loading countries...' : 'Select country' }}
                </option>
                <option *ngFor="let c of countries" [value]="c.code">
                  {{ countryFlag(c.code) }} {{ c.name }}
                </option>
              </select>
              <div *ngIf="isInvalid(adminForm, 'country')" class="invalid-feedback">
                Select a country
              </div>

              <div *ngIf="countriesLoading" class="form-text d-flex align-items-center gap-2">
                <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                Loading available countries...
              </div>

              <div *ngIf="countriesError" class="country-load-error mt-2">
                <span>{{ countriesError }}</span>
                <button type="button" class="btn btn-link btn-sm p-0 ms-1" (click)="loadMain()">
                  Retry
                </button>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label small text-muted">
                Phone number
                <span class="fw-normal">(optional)</span>
              </label>

              <div class="input-group phone-input-group">
                <span class="input-group-text phone-prefix" *ngIf="selectedCountry && selectedDialCode">
                  <span class="phone-flag">{{ selectedCountryFlag }}</span>
                  <span>{{ selectedDialCode }}</span>
                </span>

                <input type="tel"
                       inputmode="tel"
                       class="form-control"
                       formControlName="phone"
                       autocomplete="tel"
                       [placeholder]="phonePlaceholder"
                       [readonly]="!selectedCountry"
                       [class.is-invalid]="phone?.touched && phoneInvalid"
                       (input)="onAdminPhoneInput($event)" />
              </div>

              <div *ngIf="phone?.touched && phoneInvalid" class="invalid-feedback d-block">
                Enter a valid international phone number or leave it blank.
              </div>

              <div *ngIf="selectedCountry && !phoneEntrySupported" class="form-text">
                Enter the complete international number, including the country code.
                Example: <strong>+1 555 123 4567</strong>.
              </div>

              <div *ngIf="selectedCountry && phoneEntrySupported && !phoneInternationalPreview" class="form-text">
                The country code is added automatically. Do not type {{ selectedDialCode }} again.
              </div>

              <div *ngIf="phoneInternationalPreview" class="form-text phone-preview">
                The number will be saved as <strong>{{ phoneInternationalPreview }}</strong>.
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label small text-muted">Role</label>
              <select class="form-select"
                      formControlName="role"
                      [ngClass]="{ 'is-invalid': isInvalid(adminForm, 'role') }">
                <option *ngFor="let option of staffRoleOptions" [value]="option.value">
                  {{ option.label }}
                </option>
              </select>
              <div class="form-text">
                The selected role controls backend permissions, routes, menus, and available actions.
              </div>
            </div>

            <button class="btn btn-primary w-100"
                    type="submit"
                    [disabled]="adminForm.invalid || loading || countriesLoading || !!countriesError">
              <span *ngIf="!loading">Create User</span>
              <span *ngIf="loading" class="spinner-border spinner-border-sm"></span>
            </button>

            <div class="small text-muted text-center mt-2">
              The selected role is assigned immediately. A temporary password is emailed and must be changed at first sign-in.
            </div>

          </form>

        </div>
      </div>
    </div>

    <!-- RIGHT: Admin List -->
    <div class="col-12 col-lg-8">
      <div class="card shadow-sm border-0">
        <div class="card-body">

          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="fw-semibold mb-0">All Users & Staff</h6>
            <span class="badge text-bg-secondary">{{ meta?.totalRows || 0 }}</span>
          </div>

          <div class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>

              <tbody>
                <tr *ngFor="let a of admins">
                  <td>
                    <div class="fw-semibold">{{ a.firstName }} {{ a.lastName }}</div>
                    <div class="small text-muted">{{ a.role || '—' }} • {{ a.country || '—' }} • {{ a.phone || '—' }}</div>
                  </td>

                  <td>{{ a.email }}</td>

                  <td>
                    <span
                          [class]="getStatusClass(a.status)">
                      {{a.status}}
                    </span>
                  </td>

                  <td class="small text-muted">{{ a.createdAt | date:'mediumDate' }}</td>

                  <td class="text-end">
                    <button type="button"
                        class="btn btn-sm btn-outline-secondary me-2"
                        (click)="openUserModal(a)">
                    View
                    </button>
                    <button type="button" *ngIf="a.role === 'Admin'"
                        class="btn btn-sm btn-outline-primary me-2"
                        (click)="openEditModal(a)">
                      Edit
                    </button>
                    <button type="button" *ngIf="a.otp?.otpEnabled"
                        class="btn btn-sm btn-outline-danger me-2"
                        (click)="openResetOtpModal(a)">
                      Disable 2FA
                    </button>

                    <button type="button"
                        class="btn btn-sm btn-outline-danger"
                        [disabled]="deletingId === a.id"
                        (click)="disableAdmin(a.id)">
                      Disable
                    </button>
                  </td>
                </tr>

                <tr *ngIf="!admins || admins.length === 0">
                  <td colspan="5" class="text-center text-muted py-4">
                    No users found
                  </td>
                </tr>

              </tbody>
            </table>
            <app-pagination *ngIf="meta && meta.totalRows > 0" [url]='url' [counts]='meta!' (refresh)="getUsers($event)"></app-pagination>
          </div>

        </div>
      </div>
    </div>

  </div>
        <app-footer></app-footer>
  </main>
  </div>

  <!-- EDIT ADMIN MODAL -->
  <div class="modal fade" id="editAdminModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow">

        <div class="modal-header" style="background:#0b1f3a;">
          <h6 class="modal-title text-white fw-semibold">Edit Admin User</h6>
          <button type="button"
                  class="btn-close btn-close-white"
                  aria-label="Close"
                  (click)="closeEditModal()"></button>
        </div>

        <form [formGroup]="editAdminForm" (ngSubmit)="saveAdminEdits()">

          <div class="modal-body">

            <input type="hidden" formControlName="id" />

            <div class="mb-3">
              <label class="form-label small text-muted">First Name</label>
              <input type="text"
                     class="form-control"
                     formControlName="firstName"
                     [ngClass]="{ 'is-invalid': isInvalid(editAdminForm, 'firstName') }" />
              <div *ngIf="isInvalid(editAdminForm, 'firstName')" class="invalid-feedback">
                First name is required
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label small text-muted">Last Name</label>
              <input type="text"
                     class="form-control"
                     formControlName="lastName"
                     [ngClass]="{ 'is-invalid': isInvalid(editAdminForm, 'lastName') }" />
              <div *ngIf="isInvalid(editAdminForm, 'lastName')" class="invalid-feedback">
                Last name is required
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label small text-muted">Phone (optional)</label>
              <input type="text" class="form-control" formControlName="phone" />
            </div>

            <div class="mb-3">
              <label class="form-label small text-muted">Country</label>
              
              <select class="form-control"
                     formControlName="country"
                     placeholder="NG"
                     [ngClass]="{ 'is-invalid': isInvalid(editAdminForm, 'country') }">
                     <option value="">Select Country</option>
                     <option *ngFor="let c of countries" [value]="c.code">
                        {{c.name}}
                     </option>
                </select>
                <div *ngIf="isInvalid(editAdminForm, 'country')" class="invalid-feedback">
                    Select a country
                </div>
            </div>

            <div class="mb-3">
              <label class="form-label small text-muted">Status</label>
              <select class="form-select" formControlName="status">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Suspended">Suspended</option>
                <option value="Banned">Banned</option>
              </select>
            </div>

          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" (click)="closeEditModal()">
              Cancel
            </button>

            <button type="submit"
                    class="btn btn-primary"
                    [disabled]="editAdminForm.invalid || loading">
              <span *ngIf="!loading">Save Changes</span>
              <span *ngIf="loading" class="spinner-border spinner-border-sm"></span>
            </button>
          </div>

        </form>

      </div>
    </div>
  </div>
<!-- USER DETAILS MODAL -->
<div class="modal fade" id="userDetailsModal" tabindex="-1" aria-hidden="true">
  <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
    <div class="modal-content border-0 shadow">

      <div class="modal-header" style="background:#0b1f3a;">
        <div>
          <h6 class="modal-title text-white fw-semibold mb-0">User Details</h6>
          <div class="text-white-50 small">
            {{ selectedUser?.firstName }} {{ selectedUser?.lastName }} • {{ selectedUser?.email }}
          </div>
        </div>

        <button type="button"
                class="btn-close btn-close-white"
                aria-label="Close"
                (click)="closeUserModal()"></button>
      </div>

      <div class="modal-body">

        <!-- Tabs -->
        <ul class="nav nav-tabs" role="tablist">
          <li class="nav-item" role="presentation">
            <button class="nav-link active"
                    data-bs-toggle="tab"
                    data-bs-target="#tabUser"
                    type="button"
                    role="tab">
              Details
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link"
                    data-bs-toggle="tab"
                    data-bs-target="#tabCountryChange"
                    type="button"
                    role="tab">
              Country Change
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link"
                    data-bs-toggle="tab"
                    data-bs-target="#tabWallets"
                    type="button"
                    role="tab">
              Wallets
              <span class="badge text-bg-secondary ms-1">{{ metaForWallet?.totalRows || 0 }}</span>
            </button>
          </li>

          <li class="nav-item" role="presentation">
            <button class="nav-link"
                    data-bs-toggle="tab"
                    data-bs-target="#tabOrders"
                    type="button"
                    role="tab">
              Orders
              <span class="badge text-bg-secondary ms-1">{{ metaForOrder?.totalRows || 0 }}</span>
            </button>
          </li>
        </ul>

        <div class="tab-content pt-3">

          <!-- DETAILS TAB -->
          <div class="tab-pane fade show active" id="tabUser" role="tabpanel">
            <div class="row g-3">

              <div class="col-12 col-lg-6">
                <div class="p-3 rounded-3 border bg-body">
                  <div class="fw-semibold mb-2">Profile</div>

                  <div class="small text-muted">Name</div>
                  <div class="fw-semibold">{{ selectedUser?.firstName }} {{ selectedUser?.lastName }}</div>

                  <hr class="my-3">

                  <div class="small text-muted">Email</div>
                  <div class="fw-semibold">{{ selectedUser?.email }}</div>

                  <hr class="my-3">

                  <div class="small text-muted">Phone</div>
                  <div class="fw-semibold">{{ selectedUser?.phone || '—' }}</div>
                </div>
              </div>

              <div class="col-12 col-lg-6">
                <div class="p-3 rounded-3 border bg-body">
                  <div class="fw-semibold mb-2">Account</div>

                  <div class="d-flex justify-content-between">
                    <span class="small text-muted">Role</span>
                    <span class="badge text-bg-primary">{{ selectedUser?.role }}</span>
                  </div>

                  <hr class="my-3">

                  <div class="d-flex justify-content-between">
                    <span class="small text-muted">Status</span>
                    <span class="badge"
                          [ngClass]="{
                            'text-bg-success': selectedUser?.status === 'Active',
                            'text-bg-secondary': selectedUser?.status !== 'Active'
                          }">
                      {{ selectedUser?.status }}
                    </span>
                  </div>

                  <hr class="my-3">

                  <div class="d-flex justify-content-between">
                    <span class="small text-muted">Country</span>
                    <span class="fw-semibold">{{ selectedUser?.country || '—' }}</span>
                  </div>
                  <hr class="my-3">

                  <div class="d-flex justify-content-between">
                    <span class="small text-muted">Created</span>
                    <span class="fw-semibold">{{ selectedUser?.createdAt | date:'medium' }}</span>
                  </div>

                </div>
              </div>

            </div>
          </div>

          <!-- WALLETS TAB -->
          <div class="tab-pane fade" id="tabWallets" role="tabpanel">

            <div class="table-responsive">
              <table class="table table-hover align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Currency</th>
                    <th class="text-end">Available</th>
                    <th class="text-end">Ledger</th>
                    <th class="text-end">Hold</th>
                  </tr>
                </thead>

                <tbody>
                  <tr *ngFor="let w of selectedUserWallet">
                    <td class="fw-semibold">{{ w.currency.code || w.currency.symbol }}</td>
                    <td class="text-end">{{ w.availableBalance | smartAmount }}</td>
                    <td class="text-end">{{ w.balance | smartAmount }}</td>
                    <td class="text-end">{{ (w.balance - w.availableBalance) | smartAmount }}</td>
                  </tr>

                  <tr *ngIf="!selectedUserWallet || selectedUserWallet.length === 0">
                    <td colspan="4" class="text-center text-muted py-4">No wallets</td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>

          <!-- ORDERS TAB -->
          <div class="tab-pane fade" id="tabOrders" role="tabpanel">

            <div class="table-responsive">
              <table class="table table-hover align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Order</th>
                    <th>Pair</th>
                    <th class="text-end">Original</th>
                    <th class="text-end">Remaining</th>
                    <th>Status</th>
                    <th class="text-end">Created</th>
                  </tr>
                </thead>

                <tbody>
                  <tr *ngFor="let o of selectedUserOrders">
                    <td class="fw-semibold">{{ o.id }}</td>
                    <td>{{ o.fromCurrency.code }} / {{ o.toCurrency.code }}</td>
                    <td class="text-end">{{ o.originalAmount | smartAmount }}</td>
                    <td class="text-end">{{ o.remainingAmount | smartAmount }}</td>
                    <td>
                      <span [class]="getOrderStatusClass(o.status)">{{ o.status }}</span>
                    </td>
                    <td class="text-end small text-muted">{{ o.createdAt | date:'mediumDate' }}</td>
                  </tr>

                  <tr *ngIf="!selectedUserOrders || selectedUserOrders.length === 0">
                    <td colspan="6" class="text-center text-muted py-4">No orders</td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>

          <!-- COUNTRY CHANGE TAB -->
          <div class="tab-pane fade" id="tabCountryChange" role="tabpanel">
            <app-admin-country-change *ngIf="selectedUser as user"
              [userId]="user.id"
              [currentCountryCode]="user.country?.default?.code ?? ''"
              [defaultProductType]="'Wallet'">
            </app-admin-country-change>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" (click)="closeUserModal()">Close</button>
      </div>

    </div>
  </div>
</div>
<!-- USER DISABLE OTP MODAL -->
<div class="modal fade" id="openResetOtpModal" tabindex="-1" aria-hidden="true">
  <div class="modal-dialog modal modal-dialog-centered modal-dialog-scrollable">
    <div class="modal-content border-0 shadow">

      <div class="modal-header" style="background:#0b1f3a;">
        <div>
          <h6 class="modal-title text-white fw-semibold mb-0">Disable Two Factor Authentication</h6>
          <div class="text-white-50 small">
            {{ selectedUser?.firstName }} {{ selectedUser?.lastName }} • {{ selectedUser?.email }}
          </div>
        </div>

        <button type="button"
                class="btn-close btn-close-white"
                aria-label="Close"
                (click)="close2faModal()"></button>
      </div>

      <div class="modal-body">
        <p>You are disabling two factor authentication for {{ selectedUser?.firstName }} {{ selectedUser?.lastName }} ({{ selectedUser?.id }})</p>
        
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" (click)="close2faModal()">Close</button>
        <button type="button" class="btn btn-outline-danger" (click)="disable2fa(selectedUser!.id)">Disable 2FA</button>
      </div>

    </div>
  </div>
</div>